import { Fragment, useState } from 'react';
import type { CSSProperties } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useData } from '@/hooks/useData';
import { useChildProfile } from '@/services/children';
import { GlobalHeader } from '@/components/GlobalHeader';
import { SchoolCalendarSheet } from '@/components/SchoolCalendarSheet';
import { useTranslation } from 'react-i18next';

// ── Types ──────────────────────────────────────────────────────────────────────

interface TimetableEntry {
  subject: string;
  day: string;
  start_time: string;
  end_time: string;
  room: string | null;
}

interface TimetableResponse {
  entries: TimetableEntry[];
}

type CommitmentType = 'sport' | 'music' | 'work' | 'other';

interface Commitment {
  commitment_id: string;
  label: string;
  type: CommitmentType;
  day_of_week: string;
  start_time: string;
  end_time: string;
}

interface CommitmentsResponse {
  commitments: Commitment[];
}

interface FreeTimeSlot {
  day: string;
  start_time: string;
  end_time: string;
  label: string | null;
}

interface FreeTimeResponse {
  slots: FreeTimeSlot[];
}

type GridEvent =
  | { kind: 'class'; subject: string; room: string | null; day: string; start_time: string }
  | { kind: 'commitment'; label: string; type: CommitmentType; day: string; start_time: string };

// ── Constants ──────────────────────────────────────────────────────────────────

const DAYS: { labelKey: 'Mon' | 'Tue' | 'Wed' | 'Thu' | 'Fri'; key: string }[] = [
  { labelKey: 'Mon', key: 'monday'    },
  { labelKey: 'Tue', key: 'tuesday'   },
  { labelKey: 'Wed', key: 'wednesday' },
  { labelKey: 'Thu', key: 'thursday'  },
  { labelKey: 'Fri', key: 'friday'    },
];

const HOURS = [
  '08:00', '09:00', '10:00', '11:00', '12:00',
  '13:00', '14:00', '15:00', '16:00', '17:00',
];

const COMMITMENT_COLORS: Record<CommitmentType, { bg: string; text: string }> = {
  sport: { bg: '#DCFCE7', text: '#15803D' },
  music: { bg: '#EDE9FE', text: '#6D28D9' },
  work:  { bg: '#DBEAFE', text: '#1D4ED8' },
  other: { bg: '#F3F4F6', text: '#4B5563' },
};

function matchesHour(eventTime: string, gridHour: string): boolean {
  return eventTime.slice(0, 2) === gridHour.slice(0, 2);
}

// ── Component ──────────────────────────────────────────────────────────────────

export function ParentChildTimetablePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { childId } = useParams<{ childId: string }>();
  const [calendarOpen, setCalendarOpen] = useState(false);

  const { data: profile } = useChildProfile(childId ?? '');
  const { data: timetable, isLoading: isTimeLoading } = useData<TimetableResponse>(
    childId ? `/children/${childId}/timetable` : null,
  );
  const { data: commitmentsData, isLoading: isCommLoading } = useData<CommitmentsResponse>(
    childId ? `/children/${childId}/commitments` : null,
  );
  const { data: freeTimeData } = useData<FreeTimeResponse>(
    childId ? `/children/${childId}/free-time` : null,
  );

  const freeSlots = freeTimeData?.slots ?? [];
  const isLoading = isTimeLoading || isCommLoading;

  const allEvents: GridEvent[] = [
    ...(timetable?.entries ?? []).map((e): GridEvent => ({
      kind: 'class', subject: e.subject, room: e.room, day: e.day, start_time: e.start_time,
    })),
    ...(commitmentsData?.commitments ?? []).map((c): GridEvent => ({
      kind: 'commitment', label: c.label, type: c.type, day: c.day_of_week, start_time: c.start_time,
    })),
  ];

  const getEvent = (hour: string, dayKey: string): GridEvent | undefined =>
    allEvents.find((ev) => ev.day === dayKey && matchesHour(ev.start_time, hour));

  const timeLabelStyle: CSSProperties = {
    color: 'var(--text-muted)', fontSize: '0.8rem', fontWeight: 600,
    textAlign: 'center', paddingTop: '6px',
  };
  const dayHeaderStyle: CSSProperties = {
    color: 'var(--text-muted)', fontSize: '0.8rem', fontWeight: 600, textAlign: 'center',
  };

  return (
    <div className="page-center">
      <div className="phone-mockup">
        <GlobalHeader showBack backRoute={`/parent/child/${childId}`} customTitle={t('parent_student.weekly_schedule')} />
        <div className="app-content" style={{ paddingBottom: '24px' }}>

          {/* VADOStudy brand label */}
          <p style={{ margin: '0 0 2px', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: 'var(--primary)' }}>
            VADOStudy
          </p>

          <div style={{ display: 'flex', gap: '8px', margin: '12px 0 20px' }}>
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => setCalendarOpen(true)}
              style={{ flex: 1, fontSize: '0.85rem', padding: '8px' }}
            >
              {t('school_calendar.view_button')}
            </button>
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => navigate(`/parent/child/${childId}/timetable/scan`)}
              style={{ flex: 1, fontSize: '0.85rem', padding: '8px' }}
            >
              ✏️ {t('parent_student.update_schedule')}
            </button>
          </div>

          {/* ── Loading ── */}
          {isLoading && (
            <div className="loading-skeleton" style={{ height: '280px', borderRadius: '12px' }} />
          )}

          {/* ── Empty state ── */}
          {!isLoading && allEvents.length === 0 && (
            <div className="card" style={{ textAlign: 'center', padding: '32px' }}>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', margin: '0 0 16px' }}>
                {t('parent_student.no_schedule_entries')}
              </p>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => navigate(`/parent/child/${childId}/timetable/scan`)}
                style={{ width: '100%' }}
              >
                📷 {t('parent_student.upload_schedule')}
              </button>
            </div>
          )}

          {/* ── Timetable grid ── */}
          {!isLoading && allEvents.length > 0 && (
            <div style={{ width: '100%', overflowX: 'auto', paddingBottom: '8px' }}>
              <div className="timetable-grid">

                {/* Day headers */}
                <div />
                {DAYS.map((d) => (
                  <div key={d.key} style={dayHeaderStyle}>{t(`student_timetable.days.${d.labelKey}`)}</div>
                ))}

                {/* Hour rows */}
                {HOURS.map((hour) => (
                  <Fragment key={hour}>
                    <div style={timeLabelStyle}>{hour}</div>
                    {DAYS.map((d) => {
                      const ev = getEvent(hour, d.key);
                      if (!ev) return <div key={d.key} />;

                      if (ev.kind === 'class') {
                        return (
                          <div
                            key={d.key}
                            className="subject-card"
                            title={ev.room ? `${ev.subject} · ${ev.room}` : ev.subject}
                          >
                            {ev.subject}
                            {ev.room && (
                              <span style={{ display: 'block', fontSize: '0.7rem', opacity: 0.7, marginTop: '2px' }}>
                                {ev.room}
                              </span>
                            )}
                          </div>
                        );
                      }

                      const colors = COMMITMENT_COLORS[ev.type] ?? COMMITMENT_COLORS.other;
                      return (
                        <div
                          key={d.key}
                          style={{
                            background: colors.bg, color: colors.text,
                            borderRadius: '6px', padding: '4px 6px',
                            fontSize: '0.75rem', fontWeight: 600,
                            textAlign: 'center', overflow: 'hidden',
                            textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}
                          title={`${ev.label} (${ev.type})`}
                        >
                          {ev.label}
                        </div>
                      );
                    })}
                  </Fragment>
                ))}

              </div>
            </div>
          )}

          {/* ── Legend ── */}
          {!isLoading && allEvents.length > 0 && (
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ width: '12px', height: '12px', borderRadius: '3px', background: 'var(--primary-light)', display: 'inline-block' }} />
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{t('parent_student.classes_label')}</span>
              </div>
              {(['sport', 'music', 'work', 'other'] as CommitmentType[]).map((type) => {
                const hasType = allEvents.some((ev) => ev.kind === 'commitment' && ev.type === type);
                if (!hasType) return null;
                const colors = COMMITMENT_COLORS[type];
                return (
                  <div key={type} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ width: '12px', height: '12px', borderRadius: '3px', background: colors.bg, display: 'inline-block' }} />
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'capitalize' }}>{t(`student_timetable.commitment_types.${type}`)}</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── Activities ── */}
          {!isLoading && commitmentsData?.commitments && commitmentsData.commitments.length > 0 && (
            <>
              <h2 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-dark)', margin: '24px 0 10px' }}>
                {t('student_timetable.activities_heading')}
              </h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {commitmentsData.commitments.map((c) => {
                  const colors = COMMITMENT_COLORS[c.type] ?? COMMITMENT_COLORS.other;
                  return (
                    <div
                      key={c.commitment_id}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        background: 'white', borderRadius: '10px', padding: '12px 14px',
                        border: '1px solid var(--border)',
                      }}
                    >
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '3px' }}>
                          <span style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-dark)' }}>{c.label}</span>
                          <span style={{
                            background: colors.bg, color: colors.text,
                            fontSize: '0.7rem', fontWeight: 600, padding: '2px 7px', borderRadius: '999px',
                          }}>
                            {t(`student_timetable.commitment_types.${c.type}`)}
                          </span>
                        </div>
                        <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                          {t(`student_timetable.days_full.${c.day_of_week.toLowerCase()}`, { defaultValue: c.day_of_week })} · {c.start_time}–{c.end_time}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* ── Free time ── */}
          {!isLoading && freeSlots.length > 0 && (
            <>
              <h2 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-dark)', margin: '24px 0 10px' }}>
                {t('student_timetable.free_time_heading')}
              </h2>
              <p style={{ margin: '0 0 10px', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                {t('parent_student.free_time_disclaimer')}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {freeSlots.map((slot, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      background: 'white', borderRadius: '10px', padding: '12px 14px',
                      border: '1px dashed var(--success)',
                    }}
                  >
                    <div>
                      <p style={{ margin: 0, fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-dark)' }}>
                        {slot.label ?? t('student_timetable.free_block')}
                      </p>
                      <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                        {t(`student_timetable.days_full.${slot.day.toLowerCase()}`, { defaultValue: slot.day })} · {slot.start_time}–{slot.end_time}
                      </p>
                    </div>
                    <span style={{ fontSize: '1.2rem' }}>✅</span>
                  </div>
                ))}
              </div>
            </>
          )}

        </div>
        {/* No bottom tab bar — maximise schedule space */}
      </div>

      <SchoolCalendarSheet
        open={calendarOpen}
        onClose={() => setCalendarOpen(false)}
        country={profile?.country}
      />
    </div>
  );
}
